import { getLocalDateToday } from '~/DateTimeUtils';
import type {
  GenericTemplatesTokensResponseGenericTemplatesTokenResponse,
  GenericTemplatesTokensResponseTokenLobResponse,
} from '~/server_shared/openapi/data-contracts';

type TokenResponse = GenericTemplatesTokensResponseGenericTemplatesTokenResponse;

// UTF-8 BOM so Excel detects the encoding and renders accented characters correctly.
const CSV_BOM = '\ufeff';
// RFC 4180 record separator.
const ROW_SEPARATOR = '\r\n';
const GROUP_SEPARATOR = ' › ';

export const FEATURE_DISPLAY_NAME: Record<string, string> = {
  e_sign: 'E-Sign',
  video_calls: 'Video',
  email_configuration: 'Email Config',
};

export interface BuildTokensCsvOptions {
  includeSubOrganizations: boolean;
  orgLobsCount: number;
}

/**
 * Escape a single CSV field per RFC 4180: values containing a comma, double quote,
 * or newline are wrapped in double quotes and any internal double quotes are doubled.
 */
export const escapeCsvValue = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const formatTokenKey = (tokenKey: string): string => `{{ ${tokenKey} }}`;

const formatLobs = (lobs: GenericTemplatesTokensResponseTokenLobResponse[], orgLobsCount: number): string => {
  if (lobs.length === 0) {
    return 'All';
  }
  const lobNames = lobs.map((lob) => lob.display_name || lob.key);
  // A token scoped to every org LOB is shown as "All" in the table — mirror that here.
  if (orgLobsCount > 0 && lobNames.length >= orgLobsCount) {
    return 'All';
  }
  return lobNames.join(', ');
};

const formatSubOrganizations = (token: TokenResponse): string => {
  const subOrgs = token.sub_organizations ?? [];
  if (subOrgs.length === 0) {
    return 'All';
  }
  return subOrgs.map((subOrg) => subOrg.name).join(', ');
};

const formatFeatureAvailability = (token: TokenResponse): string =>
  [
    ...(token.feature_specific ?? []).map((feature) => `Only: ${FEATURE_DISPLAY_NAME[feature] ?? feature}`),
    ...(token.exclude_feature_specific ?? []).map((feature) => `Exclude: ${FEATURE_DISPLAY_NAME[feature] ?? feature}`),
  ].join(', ');

const toRow = (cells: string[]): string => cells.map(escapeCsvValue).join(',');

/**
 * Build the full CSV file contents (prefixed with a UTF-8 BOM) for the given tokens.
 * Columns mirror the View Tokens table; one record per token plus a header row.
 */
export const buildTokensCsv = (tokens: TokenResponse[], options: BuildTokensCsvOptions): string => {
  const { includeSubOrganizations, orgLobsCount } = options;

  const headers = [
    'Token Key',
    'Display Name',
    'Group',
    'Tags',
    'LOB',
    ...(includeSubOrganizations ? ['Sub-organization'] : []),
    'Feature Availability',
  ];

  const rows = tokens.map((token) =>
    toRow([
      formatTokenKey(token.token_key),
      token.display_name ?? '',
      (token.token_group ?? []).join(GROUP_SEPARATOR),
      (token.tags ?? []).join(', '),
      formatLobs(token.lobs ?? [], orgLobsCount),
      ...(includeSubOrganizations ? [formatSubOrganizations(token)] : []),
      formatFeatureAvailability(token),
    ])
  );

  return CSV_BOM + [toRow(headers), ...rows].join(ROW_SEPARATOR);
};

export const getTokensExportFilename = (): string => `tokens_export_${getLocalDateToday()}.csv`;

/** Trigger a browser download of CSV content as a UTF-8 file. */
export const downloadCsvFile = (filename: string, content: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
